<template>
  <form class="bg-white rounded-xl w-full" @submit.prevent="submitSearch">
    <div class="p-4 md:px-6 md:pt-6">
      <div ref="searchField">
        <SearchConditionField
          :chips="conditionChips"
          :keyword="keyword"
          :active="isPanelOpen"
          @update:keyword="keyword = $event"
          @activate="openPanel()"
          @removeChip="(chip) => chip.remove()"
          @clear="clearAllConditions"
          @compositionstart="handleKeywordCompositionStart"
          @compositionend="handleKeywordCompositionEnd"
          @enter="handleKeywordEnter"
          @escape="closePanel"
        />
      </div>
      <div class="flex gap-2 mt-2">
        <button
          ref="dateButton"
          type="button"
          class="border rounded-lg p-3 flex-1 overflow-hidden"
          :class="activeSheet === 'date' ? 'border-brown' : 'border-bordergray'"
          @click.prevent="toggleSheet('date')"
        >
          <div class="flex items-center">
            <p class="pr-2">
              <icon-calendar />
            </p>
            <div class="overflow-hidden">
              <p class="truncate" :class="{ 'text-gray': !datesLabel }">{{ datesLabel || '日付を選択' }}</p>
            </div>
          </div>
        </button>
        <button
          ref="capacityButton"
          type="button"
          class="border rounded-lg p-3 flex-1 overflow-hidden"
          :class="activeSheet === 'capacity' ? 'border-brown' : 'border-bordergray'"
          @click.prevent="toggleSheet('capacity')"
        >
          <div class="flex items-center">
            <p class="pr-2">
              <icon-people />
            </p>
            <div class="overflow-hidden">
              <p class="truncate">{{ capacityLabel }}</p>
            </div>
          </div>
        </button>
      </div>
    </div>
    <div class="flex items-center gap-4 px-4 pb-4 md:px-6 md:pb-6">
      <button type="submit" class="btn btn-orange flex-1">ADDressの家を探す</button>
      <a
        href="https://www.google.com/maps/d/u/0/viewer?mid=1_vUzwhGmqPfllWRsw-bbkKxSonuWeLX5&ll=36.94282771370688%2C141.8410115375&z=5"
        target="_blank"
        rel="noopener"
        class="text-brown font-bold flex items-center whitespace-nowrap flex-shrink-0"
      >
        <icon-map />
        マップ
      </a>
    </div>
    <Teleport to="body">
      <SearchPanel
        ref="panel"
        :open="isPanelOpen"
        :anchor-rect="sheetAnchorRect"
        :areas="areas"
        :checked-areas="checkedAreas"
        :checked-prefs="checkedPrefs"
        :tags="sortedTags"
        :checked-tag="checkedTag"
        :home-kinds="homeKinds"
        :room-kinds="roomKinds"
        :home-group-kinds="homeGroupKinds"
        :checked-home-kinds="checkedHomeKinds"
        :checked-room-kinds="checkedRoomKinds"
        :is-original="isOriginal"
        :has-kitchen="hasKitchen"
        :has-parking="hasParking"
        :is-parking-free="isParkingFree"
        :is-parking-reservable="isParkingReservable"
        :has-desk-chair="hasDeskChair"
        :can-meet-yamori="canMeetYamori"
        :is-member-only="isMemberOnly"
        :checked-home-group-kinds="checkedHomeGroupKinds"
        :has-auto-approval="hasAutoApproval"
        :has-discount="hasDiscount"
        :has-discount-three-nights="hasDiscountThreeNights"
        :has-discount-seven-nights="hasDiscountSevenNights"
        :favorite-only="favoriteOnly"
        :is-contracted="isContracted"
        :is-legacy-plan="isLegacyPlan"
        :is-unregistered="isUnregistered"
        :icon-original-img-src="iconOriginalImgSrc"
        @areaChanged="handleAreaChanged"
        @prefChanged="handlePrefChanged"
        @clearArea="handleClearArea"
        @tagChanged="changeTag"
        @clearTag="clearTag"
        @checkChanged="handleCheckChanged"
        @homeKindsChanged="handleHomeKindsChanged"
        @roomKindsChanged="handleRoomKindsChanged"
        @hasParkingChanged="handleHasParkingChanged"
        @parkingDetailChanged="handleParkingDetailChanged"
        @homeGroupKindsChanged="handleHomeGroupKindsChanged"
        @hasDiscountChanged="handleHasDiscountChanged"
        @discountDetailChanged="handleDiscountDetailChanged"
        @clearDetail="handleClearDetail"
        @close="closePanel"
      >
        <template #field>
          <SearchConditionField
            ref="panelField"
            :chips="conditionChips"
            :keyword="keyword"
            :active="true"
            @update:keyword="keyword = $event"
            @removeChip="(chip) => chip.remove()"
            @clear="clearAllConditions"
            @compositionstart="handleKeywordCompositionStart"
            @compositionend="handleKeywordCompositionEnd"
            @enter="handleKeywordEnter"
            @escape="closePanel"
          />
        </template>
      </SearchPanel>
      <SearchSheet
        :open="activeSheet === 'date'"
        :anchor-rect="sheetAnchorRect"
        :width="460"
        aria-label="日付の選択"
        @close="closeSheet"
      >
        <div class="modal-search-date top-search-sheet-date">
          <DateSelectFields
            ref="dateFields"
            :current-time="currentTime"
            :start-date="calendarStartDate"
            :end-date="calendarEndDate"
            :check-in-date="checkInDate"
            :check-out-date="checkOutDate"
            :show-header="false"
            @updateValue="handleUpdatedDates"
            @completed="closeSheet"
          />
        </div>
        <template #foot>
          <button type="button" class="top-search-panel-reset" @click.prevent="$refs.dateFields.clearSelection()">
            日付をクリア
          </button>
          <button type="button" class="top-search-panel-submit" @click.prevent="closeSheet">決定</button>
        </template>
      </SearchSheet>
      <SearchSheet
        :open="activeSheet === 'capacity'"
        :anchor-rect="sheetAnchorRect"
        :width="420"
        aria-label="人数の選択"
        @close="closeSheet"
      >
        <div class="modal-search-people top-search-sheet-people">
          <CapacityFields
            :adults="adults"
            :children="children"
            :preschoolers="preschoolers"
            :rooms="rooms"
            :is-legacy-plan="isLegacyPlan"
            @updateValue="handleUpdatedCapacity"
          />
        </div>
        <template #foot>
          <button type="button" class="top-search-panel-submit" @click.prevent="closeSheet">決定</button>
        </template>
      </SearchSheet>
    </Teleport>
  </form>
</template>

<script>
import DateSelectFields from '../../shared/components/search/DateSelectFields.vue'
import CapacityFields from '../../shared/components/search/CapacityFields.vue'
import SearchPanel from './SearchPanel.vue'
import SearchSheet from './SearchSheet.vue'
import SearchConditionField from './SearchConditionField.vue'

import IconCalendar from '../../shared/icons/CalendarBig.vue'
import IconPeople from '../../shared/icons/People.vue'
import IconMap from '../../shared/icons/Map.vue'

import Teleport from 'vue2-teleport'

import SearchBase from '../../shared/components/search/SearchBase.vue'

const MOBILE_MAX_WIDTH = 767

export default {
  components: {
    DateSelectFields,
    CapacityFields,
    SearchPanel,
    SearchSheet,
    SearchConditionField,
    IconCalendar,
    IconPeople,
    IconMap,
    Teleport,
  },
  extends: SearchBase,
  data() {
    return {
      // Which popover is open: null | 'panel' (area / tag / filter) | 'date' | 'capacity'
      activeSheet: null,
      panelTab: 'area',
      sheetAnchorRect: null,
    }
  },
  computed: {
    isPanelOpen() {
      return this.activeSheet === 'panel'
    },
    // Selected conditions shown as removable chips inside the input, in the same order as the panel tabs.
    conditionChips() {
      const chips = []

      this.checkedAreas.forEach((areaId) => {
        const area = this.findArea(areaId)
        if (area) {
          chips.push({ key: `area-${area.id}`, label: area.name, remove: () => this.handleAreaChanged(false, area) })
        }
      })
      this.filteredPrefs.forEach((prefId) => {
        const pref = this.prefectures.find((pref) => pref.id === prefId)
        if (pref) {
          chips.push({ key: `pref-${pref.id}`, label: pref.name, remove: () => this.handlePrefChanged(false, pref) })
        }
      })

      const tag = this.tags.find((tag) => tag.id === this.checkedTag)
      if (tag) {
        chips.push({ key: `tag-${tag.id}`, label: tag.name, remove: () => this.clearTag() })
      }

      return chips.concat(this.detailChips)
    },
    detailChips() {
      const chips = []
      const kindChips = (checked, kinds, keyPrefix, remove) => {
        checked.forEach((value) => {
          const kind = kinds.find((kind) => kind.value === value)
          if (kind) {
            chips.push({ key: `${keyPrefix}-${value}`, label: kind.name, remove: () => remove(value) })
          }
        })
      }
      kindChips(this.checkedRoomKinds, this.roomKinds, 'room', (value) => this.handleRoomKindsChanged(false, value))
      kindChips(this.checkedHomeKinds, this.homeKinds, 'home', (value) => this.handleHomeKindsChanged(false, value))
      kindChips(this.checkedHomeGroupKinds, this.homeGroupKinds, 'group', (value) =>
        this.handleHomeGroupKindsChanged(false, value)
      )

      const flags = [
        ['hasAutoApproval', '予約承認が不要な家'],
        ['isOriginal', 'オリジナル'],
        ['canMeetYamori', '家守に会える'],
        ['isMemberOnly', '会員専用'],
        ['hasDeskChair', 'デスク・チェアあり'],
        ['hasKitchen', 'キッチンあり'],
        ['favoriteOnly', 'お気に入りのみ'],
      ]
      flags.forEach(([key, label]) => {
        if (this[key]) {
          chips.push({ key, label, remove: () => this.handleCheckChanged(key, false) })
        }
      })

      if (this.hasDiscount || this.hasDiscountThreeNights || this.hasDiscountSevenNights) {
        const label = ['連泊割']
        if (this.hasDiscountThreeNights) label.push('3泊割')
        if (this.hasDiscountSevenNights) label.push('7泊割')
        chips.push({ key: 'discount', label: label.join('・'), remove: () => this.handleHasDiscountChanged(false) })
      }

      if (this.hasParking || this.isParkingFree || this.isParkingReservable) {
        const label = ['駐車場あり']
        if (this.isParkingFree) label.push('無料')
        if (this.isParkingReservable) label.push('事前予約可')
        chips.push({ key: 'parking', label: label.join('・'), remove: () => this.handleHasParkingChanged(false) })
      }

      return chips
    },
  },
  watch: {
    activeSheet(name) {
      // Keep the page still behind an open dropdown / bottom sheet.
      document.body.classList.toggle('top-search-sheet-open', !!name)
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleDocumentMouseDown)
    document.addEventListener('keydown', this.handleDocumentKeyDown)
    window.addEventListener('resize', this.updateSheetAnchor)
    window.addEventListener('scroll', this.updateSheetAnchor, true)
  },
  beforeDestroy() {
    document.body.classList.remove('top-search-sheet-open')
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
    document.removeEventListener('keydown', this.handleDocumentKeyDown)
    window.removeEventListener('resize', this.updateSheetAnchor)
    window.removeEventListener('scroll', this.updateSheetAnchor, true)
  },
  methods: {
    openPanel(tab = 'area', forceTab = false) {
      if (this.isPanelOpen && !(forceTab && this.panelTab !== tab)) {
        return
      }
      this.panelTab = tab
      this.activeSheet = 'panel'
      this.updateSheetAnchor()
      this.$refs.panel.show(tab)
      if (window.innerWidth <= MOBILE_MAX_WIDTH) {
        // The bottom sheet covers the form, so move the keyboard to the field inside the panel.
        this.$nextTick(() => this.$refs.panelField && this.$refs.panelField.focus())
      }
    },
    closePanel() {
      this.closeSheet()
    },
    toggleSheet(name) {
      if (this.activeSheet === name) {
        this.closeSheet()
        return
      }
      this.activeSheet = name
      this.updateSheetAnchor()
    },
    closeSheet() {
      this.activeSheet = null
    },
    anchorElement() {
      return { panel: this.$refs.searchField, date: this.$refs.dateButton, capacity: this.$refs.capacityButton }[this.activeSheet]
    },
    clearAllConditions() {
      this.handleClearArea()
      this.clearTag()
      this.handleClearDetail()
      this.keyword = ''
    },
    updateSheetAnchor() {
      // Sheets are teleported to body and positioned "fixed", so the KV area's overflow:hidden cannot clip them.
      const anchor = this.anchorElement()
      if (!anchor) {
        this.sheetAnchorRect = null
        return
      }
      const rect = anchor.getBoundingClientRect()
      this.sheetAnchorRect = { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width }
    },
    handleDocumentMouseDown(event) {
      if (!this.activeSheet) return

      const anchor = this.anchorElement()
      const insideAnchor = anchor && anchor.contains(event.target)
      const insideSheet = [...document.querySelectorAll('.top-search-panel')].some((panel) => panel.contains(event.target))
      if (!insideAnchor && !insideSheet) {
        this.closeSheet()
      }
    },
    handleDocumentKeyDown(event) {
      if (this.activeSheet && event.key === 'Escape') {
        this.closeSheet()
      }
    },
  },
}
</script>
